import axios from 'axios';
import { API_CONFIG } from '../config/api.config';

// Créer une instance Axios
const axiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: API_CONFIG.HEADERS,
});

// Intercepteur de requête pour ajouter le token JWT
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Pour les FormData, laisser le navigateur définir le Content-Type automatiquement
    // (pour inclure le boundary du multipart/form-data)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur de réponse pour gérer les erreurs et le refresh token
axiosInstance.interceptors.response.use(
  (response) => {
    // Si la réponse contient des données avec success: true
    if (response.data && response.data.success !== undefined) {
      return response.data;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Si l'erreur est 401 et qu'on n'a pas déjà tenté de refresh
    // MAIS ne pas essayer de refresh sur les endpoints d'authentification (login/register)
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/register')
    ) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) {
          // Pas de refresh token, rediriger vers login
          localStorage.clear();
          window.location.href = '/login';
          return Promise.reject(error);
        }

        // Tenter de rafraîchir le token
        const response = await axios.post(
          `${API_CONFIG.BASE_URL}/auth/refresh`,
          { refreshToken },
          { headers: API_CONFIG.HEADERS }
        );

        if (response.data.success && response.data.data.accessToken) {
          const { accessToken, refreshToken: newRefreshToken } = response.data.data;

          // Sauvegarder les nouveaux tokens
          localStorage.setItem('accessToken', accessToken);
          if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
          }

          // Réessayer la requête originale avec le nouveau token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        // Échec du refresh, déconnecter l'utilisateur
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Formater les erreurs de validation
    if (error.response?.data) {
      const errorData = error.response.data;

      // Format 1: Backend Spring Boot standard { message, error, status, timestamp, path }
      if (errorData.message) {
        return Promise.reject({
          code: errorData.error || errorData.status,
          message: errorData.message,
          fields: errorData.fields,
          status: error.response.status,
        });
      }

      // Format 2: Backend personnalisé { success: false, error: { code, message, fields } }
      if (!errorData.success && errorData.error) {
        return Promise.reject({
          code: errorData.error.code,
          message: errorData.error.message,
          fields: errorData.error.fields,
          status: error.response.status,
        });
      }
    }

    return Promise.reject({
      message: error.message || 'Une erreur est survenue',
      status: error.response?.status,
    });
  }
);

export default axiosInstance;
